/**
 * Owner: Masa Bayu
 * File: PrivateAuthorDashboard.tsx
 * Date:Time: 2026-10-23 08:20
 * Description: Dashboard author PRIVAT §467-C — komposisi sidebar+topbar+hero+tab
 *   (Ringkasan · Jualan · Buku Saya · Penerbitan · Analitik) dan rail kanan.
 *   SEMUA angka datang daripada `GET /v1/publisher/dashboard` (qxp-hub) melalui
 *   `buildAuthorDashboardModel` — dataset demo `private-author-data.ts` sudah
 *   dibuang, jadi tiada KPI rekaan, lencana DEMO, ulasan/pencapaian/pengikut
 *   karangan. Tab Ulasan turut dibuang: QX belum ada hujung ulasan, dan tab
 *   tanpa sumber data ialah affordance mati.
 *   §390: band <768 bercabang ke pokok mudah alih (model yang sama).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  AuthorPublicationsPanel,
  DedicatedAnalyticsScreen,
  DedicatedMyBooksScreen,
  DedicatedSalesScreen,
} from '..';
import { getMarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { PRIVATE_AUTHOR_LABELS } from './private-author-labels';
import type { PrivateTabId } from './private-author-labels';
import { buildAuthorDashboardModel } from './private-author-view';
import { usePublisherDashboard } from './publisher-dashboard';
import { PrivateAuthorSidebar } from './PrivateAuthorSidebar';
import { PrivateAuthorTopBar } from './PrivateAuthorTopBar';
import { PrivateAuthorHero } from './PrivateAuthorHero';
import { PrivateAuthorRail } from './PrivateAuthorRail';
import { PrivateAuthorSummary } from './PrivateAuthorSummary';
import { PrivateAuthorMobileDashboard } from './PrivateAuthorMobileDashboard';
import './private-author.css';

const TAB_IDS: readonly PrivateTabId[] = ['ringkasan', 'jualan', 'buku', 'penerbitan', 'analitik'];
type TabId = PrivateTabId;

const MOBILE_QUERY = '(max-width: 767px)';

/** Julat carta: bulan sebenar terakhir yang diminta pemilih tempoh. */
const PERIOD_MONTHS = [3, 6, 12];

/** Band §373 (<768) secara JS supaya pokok §390 render satu sahaja. */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(MOBILE_QUERY).matches ?? false);
  useEffect(() => {
    const mql = window.matchMedia?.(MOBILE_QUERY);
    if (mql === undefined) {
      return;
    }
    const onChange = (event: MediaQueryListEvent): void => {
      setNarrow(event.matches);
    };
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);
  return narrow;
}

export interface PrivateAuthorDashboardProps {
  readonly onNavigate: (path: string) => void;
}

function scrollTo(id: string): void {
  window.requestAnimationFrame(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

export function PrivateAuthorDashboard({ onNavigate }: PrivateAuthorDashboardProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const la = PRIVATE_AUTHOR_LABELS[profile.language];
  const dash = usePublisherDashboard();
  const [tab, setTab] = useState<TabId>('ringkasan');
  const [period, setPeriod] = useState(0);

  const model = useMemo(
    () => buildAuthorDashboardModel(dash.view, la.priceUnknown, profile.interfaceTag),
    [dash.view, la, profile],
  );
  const series = useMemo(
    () => model.series.slice(-(PERIOD_MONTHS[period] ?? 12)),
    [model.series, period],
  );

  const go = useCallback(
    (path: string): void => {
      onNavigate(path);
    },
    [onNavigate],
  );

  const showNotifications = useCallback((): void => {
    setTab('ringkasan');
    scrollTo('qxp-notifications');
  }, []);

  const isNarrow = useIsNarrow();
  if (isNarrow) {
    // §390 — pokok mudah alih (<768) replika mockup; model yang sama §467-C.
    return <PrivateAuthorMobileDashboard onNavigate={onNavigate} model={model} />;
  }

  let content: ReactNode;
  if (tab === 'ringkasan') {
    content = (
      <div className="qxp__cols">
        <PrivateAuthorSummary
          onNavigate={go}
          profile={profile}
          la={la}
          model={model}
          series={series}
        />
        <PrivateAuthorRail onNavigate={go} la={la} model={model} />
      </div>
    );
  } else if (tab === 'jualan') {
    content = <DedicatedSalesScreen onNavigate={onNavigate} />;
  } else if (tab === 'buku') {
    content = <DedicatedMyBooksScreen onNavigate={onNavigate} />;
  } else if (tab === 'penerbitan') {
    content = <AuthorPublicationsPanel onNavigate={onNavigate} />;
  } else {
    content = <DedicatedAnalyticsScreen onNavigate={onNavigate} />;
  }

  return (
    <div className="qxp" data-qxp-tab={tab}>
      <PrivateAuthorSidebar activeId="dashboard" onNavigate={go} la={la} />
      <div className="qxp__main">
        <PrivateAuthorTopBar
          onNavigate={onNavigate}
          onShowNotifications={showNotifications}
          la={la}
          model={model}
        />
        <div className="qxp__scroll">
          <PrivateAuthorHero onNavigate={onNavigate} la={la} model={model} />
          <nav className="qxp__tabs" aria-label={la.tabsAria}>
            <div className="qxp__tabbar">
              {TAB_IDS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`qxp__tab${tab === id ? ' qxp__tab--on' : ''}`}
                  aria-current={tab === id ? 'true' : undefined}
                  onClick={() => {
                    setTab(id);
                  }}
                >
                  {la.tabs[id]}
                </button>
              ))}
            </div>
            <label className="qxp__period">
              <span className="qxp__sr">{la.periodAria}</span>
              <select
                value={period}
                aria-label={la.periodAria}
                onChange={(event) => {
                  setPeriod(Number(event.target.value));
                }}
              >
                {la.periods.map((label, i) => (
                  <option key={label} value={i}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </nav>
          <div className="qxp__content">{content}</div>
        </div>
      </div>
    </div>
  );
}
